/**
 * SEO Service — Dynamic meta tag management for Open Graph, Twitter Card,
 * and Generative Engine Optimization (GEO) across all routes.
 *
 * Client-side meta tag updates for SPA navigation. Server-side injection in server.ts
 * handles social media crawlers that do not execute JavaScript.
 */

const BASE_URL = 'https://www.auratraveler.com';
import { PRICING, fmt } from '../config/pricing';
const DEFAULT_OG_IMAGE = 'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?q=80&w=2070&auto=format&fit=crop';

interface MetaTagConfig {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: string;
  keywords?: string;
}

/**
 * Per-route SEO configurations — each page gets unique, keyword-optimized
 * meta tags for both traditional SEO and GEO (AI engine discoverability).
 */
export const ROUTE_SEO: Record<string, MetaTagConfig> = {
  '/': {
    title: 'Aura Traveler — AI-Powered Luxury Travel Itinerary Generator',
    description: 'Create bespoke luxury travel itineraries in under 90 seconds with AI. Curated restaurants, hotels, flights, interactive maps, and magazine-quality itineraries across 140+ countries for discerning travelers and travel agencies.',
    url: BASE_URL,
    type: 'website',
    keywords: 'AI travel itinerary generator, luxury travel planner, bespoke travel itinerary, AI trip planner, luxury travel planning tool',
  },
  '/membership': {
    title: 'Membership & Pricing — Luxury AI Travel Plans | Aura Traveler',
    description: `Plan trips free with the AI Day Planner. Upgrade to a ${fmt(PRICING.dayPass.price)} Day Pass for one premium multi-day itinerary, or Aura Pro at ${fmt(PRICING.pro.priceYearly)}/year for ${PRICING.pro.itinerariesPerYear}. Agencies & hotels: Atelier at ${fmt(PRICING.atelier.priceMonthly)}/mo.`,
    url: BASE_URL + '/membership',
    type: 'website',
    keywords: 'travel itinerary pricing, luxury travel subscription, white-label travel software, travel agency SaaS pricing',
  },
  '/philosophy': {
    title: 'Our Philosophy — Intelligent Luxury Travel Design | Aura Traveler',
    description: 'Discover the philosophy behind Aura Traveler: where artificial intelligence meets the art of luxury travel. We believe every journey deserves the precision of a master concierge and the soul of an artist.',
    url: BASE_URL + '/philosophy',
    type: 'article',
    keywords: 'luxury travel philosophy, intelligent travel design, AI travel innovation, bespoke travel experience',
  },
  '/journal': {
    title: 'The Journal — Luxury Travel Stories & Destination Intelligence | Aura Traveler',
    description: 'Curated travel stories, destination deep-dives, and insider guides from the world of luxury travel. Expert perspectives on the finest experiences across 140+ countries.',
    url: BASE_URL + '/journal',
    type: 'blog',
    keywords: 'luxury travel blog, travel stories, destination guides, travel intelligence, curated travel content',
  },
  '/for-agencies': {
    title: 'For Travel Agencies — White-Label AI Itinerary Platform | Aura Traveler',
    description: `Supercharge your travel agency with Aura Traveler's white-label AI itinerary platform. ${PRICING.atelier.itinerariesPerMonth} bespoke itineraries a month under your brand, with social cards and concierge intelligence, from ${fmt(PRICING.atelier.priceMonthly)}/mo.`,
    url: BASE_URL + '/for-agencies',
    type: 'website',
    keywords: 'white-label travel itinerary, travel agency software, travel SaaS platform, AI itinerary for agencies, B2B travel technology',
  },
  '/for-hotels': {
    title: 'For Hotels — AI-Powered Guest Itinerary Concierge | Aura Traveler',
    description: 'Elevate your guest experience with personalized AI-generated itineraries. Aura Traveler helps luxury hotels and resorts provide bespoke travel planning as a premium concierge amenity.',
    url: BASE_URL + '/for-hotels',
    type: 'website',
    keywords: 'hotel concierge technology, guest itinerary service, luxury hotel AI, hotel guest experience, concierge software',
  },
  '/field-notes': {
    title: 'Field Notes — Destination Dispatches & Travel Intel | Aura Traveler',
    description: 'Quick-hit destination intelligence and travel dispatches from Aura Traveler\'s global network. Insider tips, seasonal highlights, and emerging luxury destinations worth watching.',
    url: BASE_URL + '/field-notes',
    type: 'blog',
    keywords: 'travel field notes, destination intelligence, luxury travel tips, emerging destinations, travel dispatches',
  },
  '/faq': {
    title: 'Frequently Asked Questions — Aura Traveler',
    description: 'Everything you need to know about Aura Traveler: pricing, itinerary generation, white-label options, supported destinations, and how our AI luxury travel platform works.',
    url: BASE_URL + '/faq',
    type: 'website',
    keywords: 'Aura Traveler FAQ, luxury travel questions, AI itinerary FAQ, travel planner help, white-label travel FAQ',
  },
  '/about': {
    title: 'About Aura Traveler — The Story Behind AI Luxury Travel',
    description: 'Meet the team and vision behind Aura Traveler. Learn how we combine artificial intelligence with deep travel expertise to create bespoke luxury itineraries for discerning travelers worldwide.',
    url: BASE_URL + '/about',
    type: 'website',
    keywords: 'about Aura Traveler, luxury travel company, AI travel team, travel technology company, bespoke travel story',
  },
  '/contact': {
    title: 'Contact Aura Traveler — Get in Touch',
    description: 'Reach out to the Aura Traveler team for partnership inquiries, agency onboarding, concierge support, or general questions about our AI luxury travel platform.',
    url: BASE_URL + '/contact',
    type: 'website',
    keywords: 'contact Aura Traveler, travel agency partnership, luxury travel support, concierge contact, travel platform inquiry',
  },
  '/destinations': {
    title: 'Destinations — Explore 140+ Countries | Aura Traveler',
    description: 'Browse Aura Traveler\'s curated destination collection spanning 140+ countries. From Tokyo to Patagonia, discover AI-powered luxury itineraries for the world\'s most extraordinary places.',
    url: BASE_URL + '/destinations',
    type: 'website',
    keywords: 'luxury travel destinations, AI travel destinations, curated destinations, world travel guide, luxury destination planner',
  },
};

/**
 * Set or create a meta tag by property or name attribute.
 * Note: setAttribute does not need HTML escaping — the browser handles encoding.
 */
const setMeta = (property: string, content: string) => {
  let el =
    document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement ||
    document.querySelector(`meta[name="${property}"]`) as HTMLMetaElement;

  if (el) {
    el.setAttribute('content', content);
  } else {
    el = document.createElement('meta');
    if (property.startsWith('twitter:')) {
      el.setAttribute('name', property);
    } else if (property.startsWith('og:')) {
      el.setAttribute('property', property);
    } else {
      el.setAttribute('name', property);
    }
    el.setAttribute('content', content);
    document.head.appendChild(el);
  }
};

/**
 * Update the canonical URL to match the current route.
 */
const setCanonical = (url: string) => {
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
  if (link) {
    link.setAttribute('href', url);
  } else {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', url);
    document.head.appendChild(link);
  }
};

/**
 * Update document meta tags dynamically. Call this when navigating to a page
 * that should have custom OG/Twitter Card previews (e.g. shared itineraries).
 */
export const updateMetaTags = (config: MetaTagConfig) => {
  // Always ensure twitter:card is set for proper large-image previews
  setMeta('twitter:card', 'summary_large_image');
  setMeta('og:site_name', 'Aura Traveler');

  if (config.title) {
    document.title = config.title;
    setMeta('og:title', config.title);
    setMeta('twitter:title', config.title);
  }
  if (config.description) {
    setMeta('description', config.description);
    setMeta('og:description', config.description);
    setMeta('twitter:description', config.description);
  }
  if (config.image) {
    setMeta('og:image', config.image);
    setMeta('twitter:image', config.image);
  }
  if (config.url) {
    setMeta('og:url', config.url);
    setCanonical(config.url);
  }
  if (config.type) {
    setMeta('og:type', config.type);
  }
  if (config.keywords) {
    setMeta('keywords', config.keywords);
  }
};

/**
 * Apply route-specific SEO tags based on the current pathname.
 * Call this from your router's navigation handler or useEffect.
 */
export const applyRouteSEO = (pathname: string) => {
  const config = ROUTE_SEO[pathname];
  if (config) {
    updateMetaTags({
      ...config,
      image: config.image || DEFAULT_OG_IMAGE,
    });
  } else {
    // Fallback to defaults for unknown routes
    resetMetaTags();
  }
};

/**
 * Reset meta tags to site-wide defaults. Call this when navigating back to
 * the main app from a shared itinerary page.
 */
export const resetMetaTags = () => {
  updateMetaTags({
    title: 'Aura Traveler — AI-Powered Luxury Travel Itineraries',
    description: 'Generate bespoke luxury travel itineraries with AI in seconds. White-label itinerary software for travel agencies — 140+ countries, magazine-quality PDFs.',
    image: DEFAULT_OG_IMAGE,
    url: BASE_URL,
    type: 'website',
    keywords: 'luxury travel itinerary, AI travel planner, bespoke travel planning, travel itinerary generator',
  });
};
